import { FolderDownloadProgress, FolderPartLocation, FolderPartsProgress } from '../utils/downloadFolderZip';

/** Single-folder zip download (the default path). */
export interface FolderDownloadProgressView {
  kind: 'progress';
  progress: FolderDownloadProgress | null;
  error: string | null;
  /** Whether retrying could help (false for e.g. an empty folder or a deleted file). */
  canRetry: boolean;
}

/** Confirmation shown when a folder is too large and must be split into several zips. */
export interface FolderDownloadConfirmView {
  kind: 'confirm-parts';
  fileCount: number;
  partCount: number;
  pageSize: number;
}

/** Progress while downloading a folder as several "part k of N" zips. */
export interface FolderDownloadPartsView {
  kind: 'parts';
  partsProgress: FolderPartsProgress | null;
  error: string | null;
  canRetry: boolean;
  /** The part that failed — where "Continue" picks up. */
  resume: Pick<FolderPartLocation, 'part' | 'totalParts'> | null;
}

export type FolderDownloadView =
  | FolderDownloadProgressView
  | FolderDownloadConfirmView
  | FolderDownloadPartsView;

interface FolderDownloadModalProps {
  folderName: string;
  view: FolderDownloadView;
  onCancel: () => void;
  onClose: () => void;
  onConfirmParts: () => void;
  /** Retry a single zip from scratch, or continue a parts run from the failed part. */
  onRetry?: () => void;
}

export function FolderDownloadModal({
  folderName,
  view,
  onCancel,
  onClose,
  onConfirmParts,
  onRetry,
}: FolderDownloadModalProps) {
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Download "{folderName}"</h2>
        {view.kind === 'progress' && (
          <SingleProgress view={view} onCancel={onCancel} onClose={onClose} onRetry={onRetry} />
        )}
        {view.kind === 'confirm-parts' && (
          <ConfirmParts
            folderName={folderName}
            view={view}
            onCancel={onCancel}
            onConfirm={onConfirmParts}
          />
        )}
        {view.kind === 'parts' && (
          <PartsProgress view={view} onCancel={onCancel} onClose={onClose} onRetry={onRetry} />
        )}
      </div>
    </div>
  );
}

function SingleProgress({
  view,
  onCancel,
  onClose,
  onRetry,
}: {
  view: FolderDownloadProgressView;
  onCancel: () => void;
  onClose: () => void;
  onRetry?: () => void;
}) {
  const { progress, error, canRetry } = view;
  const total = progress?.total ?? 0;
  const completed = progress?.completed ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const done = total > 0 && completed >= total;

  if (error) {
    const showRetry = canRetry && !!onRetry;
    return (
      <>
        <div className="error-message">{error}</div>
        <div className="modal-actions">
          <button type="button" className={showRetry ? 'btn-secondary' : 'btn-primary'} onClick={onClose}>
            Close
          </button>
          {showRetry && (
            <button type="button" className="btn-primary" onClick={onRetry}>Retry</button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="folder-download-body">
        <div className="download-progress-bar">
          <div className="download-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="download-progress-text">
          {total === 0
            ? 'Preparing…'
            : done
              ? 'Packaging zip…'
              : `${completed} of ${total} files (${pct}%)`}
        </div>
        {progress?.currentFile && !done && (
          <div className="download-current-file" title={progress.currentFile}>
            {progress.currentFile}
          </div>
        )}
      </div>
      <div className="modal-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={done}>
          Cancel
        </button>
      </div>
    </>
  );
}

function ConfirmParts({
  folderName,
  view,
  onCancel,
  onConfirm,
}: {
  folderName: string;
  view: FolderDownloadConfirmView;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { fileCount, partCount, pageSize } = view;
  const zipPlural = partCount === 1 ? '' : 's';

  return (
    <>
      <div className="folder-download-body">
        <p>
          "{folderName}" contains <strong>{fileCount.toLocaleString()} files</strong> — too many to
          download as a single zip.
        </p>
        <p>
          It will download as <strong>{partCount.toLocaleString()} zip file{zipPlural}</strong>
          {partCount > 1 && <> (“part 1 of {partCount.toLocaleString()}”, …)</>}, up to{' '}
          {pageSize.toLocaleString()} files each. Together they contain every file, with the folder
          structure preserved inside each zip.
        </p>
        <div className="folder-download-warning" role="alert">
          <strong>This is a large download.</strong> {partCount.toLocaleString()} zip file{zipPlural}{' '}
          will be saved to your computer and it may take a while.
          {partCount > 1 && ' Your browser may ask for permission to download multiple files — allow it to continue.'}{' '}
          Keep this tab open until it finishes.
        </div>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary" onClick={onConfirm}>
          Download {partCount.toLocaleString()} zip{zipPlural}
        </button>
      </div>
    </>
  );
}

function PartsProgress({
  view,
  onCancel,
  onClose,
  onRetry,
}: {
  view: FolderDownloadPartsView;
  onCancel: () => void;
  onClose: () => void;
  onRetry?: () => void;
}) {
  const { partsProgress, error, canRetry, resume } = view;

  if (error) {
    // After a multi-hour run, "which parts do I already have?" is the only thing
    // the operator needs in order to decide what to redo — so say it, and offer
    // to continue rather than making them restart the folder.
    const saved = resume ? resume.part - 1 : 0;
    const showContinue = canRetry && !!resume && !!onRetry;
    return (
      <>
        <div className="error-message">{error}</div>
        {resume && (
          <p className="folder-download-body">
            {saved > 0 ? (
              <>
                Part{saved === 1 ? '' : 's'} <strong>1–{saved.toLocaleString()}</strong> of{' '}
                {resume.totalParts.toLocaleString()} finished and {saved === 1 ? 'is' : 'are'} saved to your
                computer; it stopped on part {resume.part.toLocaleString()}.
              </>
            ) : (
              <>It stopped on part {resume.part.toLocaleString()} of {resume.totalParts.toLocaleString()}; nothing has been saved yet.</>
            )}
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className={showContinue ? 'btn-secondary' : 'btn-primary'} onClick={onClose}>
            Close
          </button>
          {showContinue && (
            <button type="button" className="btn-primary" onClick={onRetry}>
              Continue from part {resume.part.toLocaleString()} of {resume.totalParts.toLocaleString()}
            </button>
          )}
        </div>
      </>
    );
  }

  const total = partsProgress?.totalParts ?? 0;
  const completed = partsProgress?.completedParts ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const done = !!partsProgress && partsProgress.currentPart === null;

  return (
    <>
      <div className="folder-download-body">
        <div className="download-progress-bar">
          <div className="download-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        {done ? (
          <div className="download-progress-text">
            Downloaded {total.toLocaleString()} zip{total === 1 ? '' : 's'}.
          </div>
        ) : (
          <>
            <div className="download-progress-text">
              Part {(partsProgress?.currentPart ?? 0).toLocaleString()} of {total.toLocaleString()}
              {partsProgress?.fileProgress && partsProgress.fileProgress.total > 0 && (
                <> · {partsProgress.fileProgress.completed} of {partsProgress.fileProgress.total} files</>
              )}
            </div>
            {partsProgress?.fileProgress?.currentFile && (
              <div className="download-current-file" title={partsProgress.fileProgress.currentFile}>
                {partsProgress.fileProgress.currentFile}
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal-actions">
        {done ? (
          <button type="button" className="btn-primary" onClick={onClose}>Done</button>
        ) : (
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </>
  );
}
